import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * Financial Configuration Service
 * API client for financial reporting configuration
 * Manages store-QB mappings, brand rules, QB account categories, cost allocations, and reconciliation rules
 */

// ============================================================================
// STORE-QB ENTITY MAPPING
// ============================================================================

export const getStores = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/stores`, { params });
  return response.data;
};

export const getStoreMappings = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/store-mappings`, { params });
  return response.data;
};

export const createStoreMapping = async (mappingData) => {
  const response = await api.post(`${API_BASE}/api/financial-config/store-mappings`, mappingData);
  return response.data;
};

export const updateStoreMapping = async (id, mappingData) => {
  const response = await api.put(`${API_BASE}/api/financial-config/store-mappings/${id}`, mappingData);
  return response.data;
};

export const deleteStoreMapping = async (id) => {
  const response = await api.delete(`${API_BASE}/api/financial-config/store-mappings/${id}`);
  return response.data;
};

export const suggestQBEntity = async (storeId, brandId) => {
  const response = await api.post(`${API_BASE}/api/financial-config/store-mappings/suggest`, {
    store_id: storeId,
    brand_id: brandId
  });
  return response.data;
};

export const getAutoSuggestions = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/store-mappings/auto-suggestions`, { params });
  return response.data;
};

// ============================================================================
// BRAND DEFAULT RULES
// ============================================================================

export const getBrandRules = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/brand-rules`, { params });
  return response.data;
};

export const createBrandRule = async (ruleData) => {
  const response = await api.post(`${API_BASE}/api/financial-config/brand-rules`, ruleData);
  return response.data;
};

export const updateBrandRule = async (id, ruleData) => {
  const response = await api.put(`${API_BASE}/api/financial-config/brand-rules/${id}`, ruleData);
  return response.data;
};

export const deleteBrandRule = async (id) => {
  const response = await api.delete(`${API_BASE}/api/financial-config/brand-rules/${id}`);
  return response.data;
};

// ============================================================================
// QB ACCOUNT CATEGORIES
// ============================================================================

export const getQBAccounts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/qb-accounts`, { params });
  return response.data;
};

export const createQBAccount = async (accountData) => {
  const response = await api.post(`${API_BASE}/api/financial-config/qb-accounts`, accountData);
  return response.data;
};

export const updateQBAccount = async (id, accountData) => {
  const response = await api.put(`${API_BASE}/api/financial-config/qb-accounts/${id}`, accountData);
  return response.data;
};

export const getUnmappedQBAccounts = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/qb-accounts/unmapped`, { params });
  return response.data;
};

// ============================================================================
// QB ACCOUNT SUGGESTIONS (LLM-powered)
// ============================================================================

export const generateQBAccountSuggestions = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/qb-accounts/generate-suggestions`, {}, { params });
  return response.data;
};

export const getQBAccountSuggestions = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/qb-accounts/suggestions`, { params });
  return response.data;
};

export const bulkAcceptSuggestions = async (accountIds) => {
  const response = await api.post(`${API_BASE}/api/financial-config/qb-accounts/accept-suggestions`, {
    account_ids: accountIds
  });
  return response.data;
};

export const rejectAllSuggestions = async () => {
  const response = await api.delete(`${API_BASE}/api/financial-config/qb-accounts/suggestions`);
  return response.data;
};

// ============================================================================
// COST ALLOCATION OVERRIDES
// ============================================================================

export const getCostAllocations = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/financial-config/cost-allocations`, { params });
  return response.data;
};

export const createCostAllocation = async (allocationData) => {
  const response = await api.post(`${API_BASE}/api/financial-config/cost-allocations`, allocationData);
  return response.data;
};

export const updateCostAllocation = async (id, allocationData) => {
  const response = await api.put(`${API_BASE}/api/financial-config/cost-allocations/${id}`, allocationData);
  return response.data;
};

export const deleteCostAllocation = async (id) => {
  const response = await api.delete(`${API_BASE}/api/financial-config/cost-allocations/${id}`);
  return response.data;
};

export const previewAllocation = async (allocationData) => {
  const response = await api.post(`${API_BASE}/api/financial-config/cost-allocations/preview`, allocationData);
  return response.data;
};

// ============================================================================
// RECONCILIATION RULES
// ============================================================================

export const getReconciliationRules = async () => {
  const response = await api.get(`${API_BASE}/api/financial-config/reconciliation-rules`);
  return response.data;
};

export const updateReconciliationRules = async (id, rulesData) => {
  const response = await api.put(`${API_BASE}/api/financial-config/reconciliation-rules/${id}`, rulesData);
  return response.data;
};

// ============================================================================
// P&L GENERATION (Phase 2)
// ============================================================================

export const generateIncomeStatement = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/pl/generate`, params);
  return response.data;
};

export const reconcilePLWithQB = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/pl/reconcile`, params);
  return response.data;
};

// Balance Sheet (2026-05-02)
export const generateBalanceSheet = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/balance-sheet/generate`, params);
  return response.data;
};

// Cash Flow Statement (2026-05-02)
export const generateCashFlowStatement = async (params = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/cash-flow/generate`, params);
  return response.data;
};

// Drill-down for a single P&L cell. Returns formula breakdown for calc rows
// or aggregate/transaction view for leaf data rows.
//
// csvExport=true bumps the row cap to 100k so a CSV captures the full dataset
// (otherwise default limit is 100 / cap 500).
export const drillDownPL = async ({
  row_id,
  period = null,
  drill_path = [],
  filters,
  limit = 100,
  offset = 0,
  pl_payload = null,
  csvExport = false,
} = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/pl/drilldown`, {
    row_id, period, drill_path, filters, limit, offset, pl_payload, csvExport,
  });
  return response.data;
};

// Order ID / order name lookup — surfaces matching DTC + Retail orders across
// all brand DBs. Used by the drill-drawer search box.
export const searchOrders = async ({ query, filters = {}, limit = 50 } = {}) => {
  const response = await api.post(`${API_BASE}/api/financial-config/pl/order-search`, {
    query, filters, limit,
  });
  return response.data;
};

// Monthly P&L Excel export — multi-sheet workbook for accountant verification.
// Returns the raw .xlsx as a Blob; caller triggers download.
export const exportMonthlyExcel = async ({ month, brand_id = null, market = null, channel = null, basis = null } = {}) => {
  const response = await api.post(
    `${API_BASE}/api/financial-config/pl/export-monthly-excel`,
    { month, brand_id, market, channel, basis },
    { responseType: 'blob' },
  );
  return response.data;
};

// All-bank-transactions Excel export — single sheet, one row per bank txn
// across amex/revolut/wise/paypal with JE/GL booking info.
// Pass startDate + endDate (YYYY-MM-DD) for an arbitrary range; `month`
// (YYYY-MM-01) is the legacy whole-calendar-month form.
export const exportMonthlyBankTransactions = async ({ month, startDate, endDate } = {}) => {
  const body = (startDate || endDate)
    ? { start_date: startDate, end_date: endDate }
    : { month };
  const response = await api.post(
    `${API_BASE}/api/finance/transactions/export-monthly-excel`,
    body,
    { responseType: 'blob' },
  );
  return response.data;
};

// ============================================================================
// JOURNAL ENTRY RECLASSIFY (drilldown action)
// ============================================================================

// ===== Accrual vendor substitutions =====
// Vendors whose payment-dated JEs are shown as invoice-dated bills on the accrual P&L.
// Used to require a code change + deploy; now editable here.

export const getAccrualVendors = async () => {
  const response = await api.get(`${API_BASE}/api/financial-config/accrual-vendors`);
  return response.data;
};

// Invoice coverage by month — surfaced in the dialog so you can see BEFORE saving that a
// vendor has thin coverage (months without an invoice stay cash-timed rather than vanish).
export const getAccrualVendorCoverage = async (supplierPattern) => {
  const response = await api.get(`${API_BASE}/api/financial-config/accrual-vendors/coverage`, {
    params: { supplier_pattern: supplierPattern },
  });
  return response.data;
};

export const saveAccrualVendor = async (vendor) => {
  const response = vendor.id
    ? await api.put(`${API_BASE}/api/financial-config/accrual-vendors/${vendor.id}`, vendor)
    : await api.post(`${API_BASE}/api/financial-config/accrual-vendors`, vendor);
  return response.data;
};

export const deleteAccrualVendor = async (id) => {
  const response = await api.delete(`${API_BASE}/api/financial-config/accrual-vendors/${id}`);
  return response.data;
};

export const getJournalEntryDetail = async (jeId) => {
  const response = await api.get(`${API_BASE}/api/finance/journal-entries/${jeId}`);
  return response.data;
};

export const reclassifyJournalEntry = async (jeId, payload) => {
  const response = await api.post(
    `${API_BASE}/api/finance/journal-entries/${jeId}/reclassify`,
    payload,
  );
  return response.data;
};

// Bulk vendor reclassify — fetch candidate JEs matching the trigger JE's
// vendor pattern within a date range.
export const getBulkReclassifyPreview = async (jeId, { startDate, endDate } = {}) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await api.get(
    `${API_BASE}/api/finance/journal-entries/${jeId}/bulk-reclassify-preview`,
    { params },
  );
  return response.data;
};

// Bulk vendor reclassify — void+recreate the user-confirmed JEs.
export const bulkReclassifyJournalEntry = async (jeId, payload) => {
  const response = await api.post(
    `${API_BASE}/api/finance/journal-entries/${jeId}/bulk-reclassify`,
    payload,
  );
  return response.data;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const EXPENSE_CATEGORIES = [
  { value: 'salaries', label: 'Salaries & Payroll' },
  { value: 'rent', label: 'Rent & Facilities' },
  { value: 'software', label: 'Software & SaaS' },
  { value: 'fulfillment', label: 'Fulfillment & Shipping' },
  { value: 'marketing_other', label: 'Marketing (Other)' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'operating_other', label: 'Operating Expenses (Other)' },
  { value: 'cogs', label: 'Cost of Goods Sold' },
  { value: 'revenue', label: 'Revenue' },
  { value: 'exclude', label: 'Exclude from P&L' }
];

export const ALLOCATION_METHODS = [
  { value: 'revenue_based', label: 'Revenue-Based (Automatic)' },
  { value: 'manual_percentage', label: 'Manual Percentage Override' },
  { value: 'headcount_based', label: 'Headcount-Based (Future)' },
  { value: 'exclude', label: 'Exclude Brand from This Entity' },
  { value: 'direct', label: 'Direct Attribution (Already Tagged)' }
];

export const MAPPING_METHODS = [
  { value: 'manual', label: 'Manual' },
  { value: 'currency_inferred', label: 'Currency Inferred' },
  { value: 'brand_inferred', label: 'Brand Rule Inferred' },
  { value: 'geography_inferred', label: 'Geography Inferred' }
];

export default {
  // Store mappings
  getStores,
  getStoreMappings,
  createStoreMapping,
  updateStoreMapping,
  deleteStoreMapping,
  suggestQBEntity,
  getAutoSuggestions,

  // Brand rules
  getBrandRules,
  createBrandRule,
  updateBrandRule,
  deleteBrandRule,

  // QB accounts
  getQBAccounts,
  createQBAccount,
  updateQBAccount,
  getUnmappedQBAccounts,

  // QB account suggestions (LLM)
  generateQBAccountSuggestions,
  getQBAccountSuggestions,
  bulkAcceptSuggestions,
  rejectAllSuggestions,

  // Cost allocations
  getCostAllocations,
  createCostAllocation,
  updateCostAllocation,
  deleteCostAllocation,
  previewAllocation,

  // Reconciliation rules
  getReconciliationRules,
  updateReconciliationRules,

  // P&L Generation
  generateIncomeStatement,
  reconcilePLWithQB,
  drillDownPL,
  searchOrders,
  exportMonthlyExcel,
  exportMonthlyBankTransactions,

  // JE reclassify
  getJournalEntryDetail,
  reclassifyJournalEntry,
  getBulkReclassifyPreview,
  bulkReclassifyJournalEntry,

  // Accrual vendor substitutions
  getAccrualVendors,
  getAccrualVendorCoverage,
  saveAccrualVendor,
  deleteAccrualVendor,

  // Constants
  EXPENSE_CATEGORIES,
  ALLOCATION_METHODS,
  MAPPING_METHODS,
};
